import { clientApi } from './clientApi';

/**
 * The door plaque — https://checkout.hostme.sa/#/c/<token>
 * tasks/checkout-tap-url.md step 9 · tasks/checkout-token-out-of-url.md.
 *
 * 🔴 `clientApi`, NEVER `secureApi`. There is no user here: the credential is a token printed on a
 * tag beside the apartment door. `secureApi` attaches the operator's JWT from localStorage and
 * redirects to /login on a 401 — both catastrophic on a page a guest opens with a phone.
 *
 * 🔴 THE TOKEN GOES IN A HEADER, NEVER IN THE URL (D73). These three calls used to carry it as a
 * path segment, and it was measured on production landing in nginx's `access.log` in full: our
 * redaction reaches this app's console and Sentry, not the web server in front of it. The plaque
 * token is the one credential in the product that can never be rotated — it is screwed to a wall —
 * so it must not be written to a file at all. A header is absent from nginx's log format, from
 * `Referer`, and from browser history.
 *
 * ⚠️ Set PER CALL, never as an interceptor on `clientApi`. That instance is shared with the client
 * portal, which has no business emitting this header on requests that have nothing to do with a
 * plaque.
 *
 * ⚠️ The PRINTED URL is unchanged and must stay unchanged (D74) — only this transport moved.
 *
 * The API answers with a STATE, plus which apartment the plaque is on (D79) — and nothing else. No
 * guest name, no booking reference, no dates, and deliberately not whether the unit is occupied, so
 * every quiet case looks identical from here and this module must not try to tell them apart.
 *
 * ⚠️ The unit label rides on the `status` call ONLY (D82). The press and the undo answer with a state
 * alone; the page holds the label across both. Do not "even it up" by adding the field to the write
 * responses — and never make it conditional on there being something to do, or its presence becomes
 * the occupancy answer the uniform states exist to withhold.
 */

const BASE = '/api/v1/public/checkout-tap';

/** The capability header. Lower-case on the wire either way; named here as it appears in CORS. */
const tokenHeader = (token) => ({ headers: { 'X-Checkout-Token': token } });

const checkoutTapApi = {
  /** Is there a departure to confirm in this apartment right now? */
  status: async (token) => {
    const { data } = await clientApi.get(BASE, tokenHeader(token));
    return data;
  },

  /** "I have left." The press — never the tap (D38). */
  confirm: async (token) => {
    // ⚠️ `{}`, NOT `null`. The token is the whole request and the endpoint reads no body — but
    // `clientApi` sets `Content-Type: application/json` on every request, so a `null` body is sent
    // as the four characters `null`, and the strict JSON parser rejects that with
    // «Unexpected token 'n'». The page then showed «تعذّر الاتصال» — a connection error, for a
    // request that connected perfectly.
    //
    // The test suite could not have caught it: supertest sends neither a body nor a content-type,
    // so the parser never ran. It took a real browser. An empty object is two bytes against a 1kb
    // cap and removes the question entirely.
    const { data } = await clientApi.post(BASE, {}, tokenHeader(token));
    return data;
  },

  /**
   * "I pressed that by mistake." Valid for 15 minutes, from the device that pressed.
   * The capability is the authorisation — hold it in memory only, never in storage.
   */
  undo: async (token, undoToken) => {
    const { data } = await clientApi.post(`${BASE}/undo`, { undoToken }, tokenHeader(token));
    return data;
  }
};

export default checkoutTapApi;
